import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../../context/StateContext";
import { Brand } from "../../../../../../../../models/brand";
import { Filters, Searches } from "../../../../../../../../models/shipeezi.table";
import brandService from "../../../../../../../../services/brand.service";
import CartonReportService from "../../../../../../../../services/carton.report.service";
import { checkError } from "../../../../../../../../util/checkError";
import getRelatedFilters from "../../../../../../../../util/getRelatedFilters";
import { BrandKeys } from "../../../../../../../../util/queries";

const CACHE_TIME = 60 * 60 * 1000; // 1 hour

interface BrandProps {
  brand: Brand;
}

interface UpdateBrandProps extends BrandProps {
  id: number;
}

const useAction = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const getAllBrands = (
    page: number = 1,
    pageSize: number = 20,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: BrandKeys.getAndCountAll([page, pageSize, searches, filters]),
      queryFn: async () => {
        const response = await brandService.getAndCountAll({
          page,
          pageSize,
          groupBy: JSON.stringify(["id"]),
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          ...getRelatedFilters(filters),
          include: JSON.stringify([{ association: "logo" }]),
          companyId: currentUser?.companyId,
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.id,
      staleTime: 3 * 60 * 1000, // 3 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const useCreateBrand = (callback?: () => void) => {
    return useMutation({
      mutationKey: BrandKeys.save(),
      mutationFn: ({ brand }: BrandProps) => {
        return brandService.create({ ...brand, companyId: currentUser?.companyId });
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callback) {
          callback();
        }
      },
    });
  };
  const useUpdateBrand = (callback?: () => void) => {
    return useMutation({
      mutationKey: BrandKeys.save(),
      mutationFn: ({ brand, id }: UpdateBrandProps) => {
        return brandService.update(id, { ...brand });
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callback) {
          callback();
        }
      },
    });
  };

  const useUpdateBrandList = (callback?: () => void) => {
    return useMutation({
      mutationKey: BrandKeys.save(),
      mutationFn: (brands: Brand[]) => {
        return brandService.updateList(brands);
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callback) {
          callback();
        }
      },
    });
  };

  const useDeleteBrands = (id: number, callback?: () => void) => {
    return useMutation({
      mutationKey: BrandKeys.delete(id),
      mutationFn: (id: number) => {
        return brandService.delete(id);
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callback) {
          callback();
        }
      },
    });
  };
  const useDeleteBrandsList = (ids: number[], callback?: () => void) => {
    return useMutation({
      mutationKey: BrandKeys.deleteList(ids),
      mutationFn: (brands: Brand[]) => {
        return brandService.deleteList(brands);
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callback) {
          callback();
        }
      },
    });
  };

  const useGetPreviewLabel = (callback?: (buffer: Blob) => void) => {
    return useMutation({
      mutationKey: BrandKeys.generatePreview(),
      mutationFn: (brand?: Brand) => {
        return CartonReportService.generatePreview(brand);
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
      onSuccess: (response) => {
        if (response?.data) {
          if (response?.data?.content) {
            if (callback) {
              const blob = new Blob([new Uint8Array(response?.data?.content).buffer], {
                type: "application/pdf",
              });

              callback(blob);
            }
          }
        }
      },
    });
  };

  return {
    getAllBrands,
    useCreateBrand,
    useDeleteBrands,
    useUpdateBrand,
    useUpdateBrandList,
    useDeleteBrandsList,
    useGetPreviewLabel,
  };
};

export default useAction;
